import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  ArrowRight,
  BookOpenCheck,
  CalendarCheck2,
  Check,
  ClipboardCheck,
  Compass,
  Copy,
  Globe2,
  GraduationCap,
  MapPinned,
  MessageCircleMore,
  SearchCheck,
  Sparkles,
  TicketPercent,
  TvMinimalPlay,
  UsersRound,
  Video,
  X,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  allRegionsProgram,
  mentoringPrograms,
  regionalPrograms,
} from "../data/mentoringPrograms";
import settings from "../constants/settings";
import { trackEvent } from "../utils/analyticsUtils";
import mentoringSession from "../assets/editorial/mentoring-zoom.webp";
import pelangiPhoto from "../assets/stories/pelangi-al-azhar.webp";
import acceptancePhoto from "../assets/stories/student-acceptance-egypt.webp";
import departurePhoto from "../assets/stories/departure-students.webp";
import egyptStudentsPhoto from "../assets/stories/students-in-egypt.webp";
import malaysiaPhoto from "../assets/stories/universiti-malaya.webp";
import "./MentoringHome.css";

const benefits = [
  {
    icon: Video,
    title: "Mentoring Rutin",
    description:
      "Bahas peluang, progres, dan langkah berikutnya bersama mentor melalui Zoom.",
  },
  {
    icon: SearchCheck,
    title: "Informasi Terkurasi",
    description:
      "Temukan peluang yang relevan tanpa harus menyaring semuanya sendirian.",
  },
  {
    icon: TvMinimalPlay,
    title: "Webinar dan Rekaman",
    description:
      "Pelajari peluang serta strategi persiapan melalui sesi bulanan.",
  },
  {
    icon: CalendarCheck2,
    title: "Kalender Beasiswa",
    description:
      "Pantau periode pendaftaran dan mulai menyiapkan diri lebih awal.",
  },
  {
    icon: ClipboardCheck,
    title: "Checklist dan Timeline",
    description:
      "Ubah persyaratan menjadi langkah yang jelas dan mudah dipantau.",
  },
  {
    icon: BookOpenCheck,
    title: "Buku PDF dan Template",
    description:
      "Gunakan materi pendukung saat menyiapkan berkas dan tes terkait.",
  },
];

const faqs = [
  {
    question: "Apa perbedaan program satu wilayah dan Paket Semua Wilayah?",
    answer:
      "Program satu wilayah cocok saat tujuan utamamu sudah jelas. Paket Semua Wilayah memberi akses ke program Indonesia, Asia Pasifik, Timur Tengah, dan Eropa agar kamu dapat membandingkan beberapa jalur.",
  },
  {
    question: "Apa saja yang saya dapat selama program?",
    answer:
      "Kamu mendapat Mentoring Rutin, informasi beasiswa terkurasi, webinar bulanan beserta rekaman, kalender beasiswa, checklist, timeline, buku PDF, dan template persiapan.",
  },
  {
    question: "Bagaimana Mentoring Rutin dilaksanakan?",
    answer:
      "Kamu dapat membahas progres dan langkah berikutnya bersama tim #PintarPilihKuliah melalui Zoom.",
  },
  {
    question: "Berapa lama akses program berlaku?",
    answer: "Akses Program Mentoring Beasiswa berlaku selama satu tahun.",
  },
  {
    question: "Apakah program menjamin saya lolos beasiswa?",
    answer:
      "Keputusan akhir berada pada kampus atau lembaga pemberi beasiswa. Program membantu kamu memahami peluang dan menyiapkan proses pendaftaran dengan lebih terarah.",
  },
  {
    question: "LIPIA masuk program mana?",
    answer: "LIPIA Jakarta termasuk Program Mentoring Beasiswa Indonesia.",
  },
  {
    question: "Bagaimana cara menggunakan kode promo?",
    answer:
      "Salin salah satu kode yang tersedia, lanjutkan ke halaman pembayaran, lalu masukkan kode tersebut pada kolom promo. Ketersediaan dan ketentuan promo mengikuti halaman pembayaran.",
  },
];

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "#PintarPilihKuliah",
  url: "https://pintarpilihkuliah.com/",
  sameAs: [
    "https://instagram.com/pintarpilihkuliah",
    "https://youtube.com/@pintarpilihkuliah",
  ],
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Program Mentoring Beasiswa #PintarPilihKuliah",
  description:
    "Pilih Program Mentoring Beasiswa Indonesia, Asia Pasifik, Timur Tengah, Eropa, atau Paket Semua Wilayah.",
  url: "https://pintarpilihkuliah.com/",
  inLanguage: "id-ID",
};

const serviceListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Program Mentoring Beasiswa #PintarPilihKuliah",
  itemListElement: mentoringPrograms.map((program, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Service",
      name: program.name,
      description: program.description,
      url: program.checkoutUrl,
      provider: {
        "@type": "Organization",
        name: "#PintarPilihKuliah",
      },
    },
  })),
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

const scrollToId = (id) => {
  const element = document.getElementById(id);
  if (!element) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  element.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  });
};

const RETENTION_OFFER_STORAGE_KEY = "p2k-retention-offer";
const LEGACY_RETENTION_OFFER_KEY = "p2k-retention-offer-seen";
const RETENTION_OFFER_TTL = 24 * 60 * 60 * 1000;

const readSavedRetentionOffer = () => {
  try {
    const savedOffer = JSON.parse(
      window.localStorage.getItem(RETENTION_OFFER_STORAGE_KEY),
    );

    if (
      !savedOffer?.shownAt ||
      Date.now() - savedOffer.shownAt > RETENTION_OFFER_TTL
    ) {
      window.localStorage.removeItem(RETENTION_OFFER_STORAGE_KEY);
      return null;
    }

    return savedOffer;
  } catch (error) {
    return null;
  }
};

const saveRetentionOffer = (offer) => {
  try {
    window.localStorage.setItem(
      RETENTION_OFFER_STORAGE_KEY,
      JSON.stringify(offer),
    );
  } catch (error) {
    // The offer remains available in memory when storage is restricted.
  }
};

const MentoringHome = () => {
  const [decision, setDecision] = useState(null);
  const [copiedCoupon, setCopiedCoupon] = useState("");
  const [copyError, setCopyError] = useState("");
  const [retentionProgramId, setRetentionProgramId] = useState(
    allRegionsProgram.id,
  );
  const [showExitOffer, setShowExitOffer] = useState(false);
  const [showOfferRecovery, setShowOfferRecovery] = useState(false);
  const [retentionCouponCopied, setRetentionCouponCopied] = useState(false);
  const interestProgramIdRef = useRef(allRegionsProgram.id);
  const checkoutStartedRef = useRef(false);
  const retentionOfferSeenRef = useRef(false);

  const retentionProgram =
    mentoringPrograms.find((program) => program.id === retentionProgramId) ||
    allRegionsProgram;
  const retentionCoupon = retentionProgram.coupons[0];

  useEffect(() => {
    let armed = false;
    let hiddenAt = 0;

    const savedOffer = readSavedRetentionOffer();
    const savedProgramExists = mentoringPrograms.some(
      (program) => program.id === savedOffer?.programId,
    );

    if (savedOffer && savedProgramExists) {
      retentionOfferSeenRef.current = true;
      interestProgramIdRef.current = savedOffer.programId;
      setRetentionProgramId(savedOffer.programId);
      setRetentionCouponCopied(Boolean(savedOffer.copied));
      setShowOfferRecovery(true);
    } else if (savedOffer) {
      try {
        window.localStorage.removeItem(RETENTION_OFFER_STORAGE_KEY);
      } catch (error) {
        // Ignore storage errors and allow a fresh offer in this visit.
      }
    }

    try {
      window.sessionStorage.removeItem(LEGACY_RETENTION_OFFER_KEY);
    } catch (error) {
      // Legacy session storage may be unavailable in restricted browsers.
    }

    const armTimer = window.setTimeout(() => {
      armed = true;
    }, 12000);

    const revealOffer = () => {
      if (!armed || checkoutStartedRef.current || retentionOfferSeenRef.current)
        return;

      const programId = interestProgramIdRef.current;
      retentionOfferSeenRef.current = true;
      setRetentionProgramId(programId);
      setShowExitOffer(true);
      setShowOfferRecovery(false);
      saveRetentionOffer({
        programId,
        shownAt: Date.now(),
        copied: false,
      });
      trackEvent("Conversion", "retention_offer_show", programId);
    };

    const handleMouseOut = (event) => {
      if (!event.relatedTarget && event.clientY <= 8) revealOffer();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }

      if (hiddenAt && Date.now() - hiddenAt > 1200) revealOffer();
    };

    document.addEventListener("mouseout", handleMouseOut);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener("mouseout", handleMouseOut);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleDecision = (nextDecision) => {
    setDecision(nextDecision);
    if (nextDecision === "compare") {
      interestProgramIdRef.current = allRegionsProgram.id;
    }
    trackEvent("Conversion", "program_path_select", nextDecision);

    window.requestAnimationFrame(() => {
      scrollToId(
        nextDecision === "compare" ? "paket-semua-wilayah" : "program-wilayah",
      );
    });
  };

  const handleCheckout = (program, placement) => {
    checkoutStartedRef.current = true;
    interestProgramIdRef.current = program.id;
    trackEvent("Conversion", "program_select", program.id);
    trackEvent("Conversion", "checkout_click", `${program.id}:${placement}`);
  };

  const handleDismissRetentionOffer = (placement) => {
    setShowExitOffer(false);
    setShowOfferRecovery(true);
    trackEvent("Conversion", "retention_offer_dismiss", placement);
  };

  const handleReopenRetentionOffer = () => {
    setShowOfferRecovery(false);
    setShowExitOffer(true);
    trackEvent("Conversion", "retention_offer_reopen", retentionProgram.id);
  };

  const handleCopyCoupon = async (code, programId) => {
    try {
      if (!navigator.clipboard) throw new Error("Clipboard API tidak tersedia");
      await navigator.clipboard.writeText(code);
      setCopiedCoupon(code);
      setCopyError("");
      setRetentionCouponCopied(true);
      setShowOfferRecovery(true);
      const savedOffer = readSavedRetentionOffer();
      saveRetentionOffer({
        programId,
        shownAt: savedOffer?.shownAt || Date.now(),
        copied: true,
      });
      trackEvent("Conversion", "coupon_copy", `${programId}:${code}`);
      window.setTimeout(() => setCopiedCoupon(""), 3500);
    } catch (error) {
      setCopyError(programId);
    }
  };

  return (
    <div className="mentoring-landing">
      <Helmet>
        <html lang="id-ID" />
        <title>
          Mentoring Beasiswa Dalam &amp; Luar Negeri | #PintarPilihKuliah
        </title>
        <meta
          name="description"
          content="Program mentoring beasiswa untuk Indonesia, Asia Pasifik, Timur Tengah, Eropa, atau semua wilayah. Temukan peluang dan siapkan pendaftaran lebih terarah."
        />
        <link rel="canonical" href="https://pintarpilihkuliah.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="id_ID" />
        <meta property="og:site_name" content="#PintarPilihKuliah" />
        <meta
          property="og:title"
          content="Program Mentoring Beasiswa | #PintarPilihKuliah"
        />
        <meta
          property="og:description"
          content="Pilih satu wilayah tujuan atau bandingkan empat wilayah dalam satu paket mentoring beasiswa."
        />
        <meta property="og:url" content="https://pintarpilihkuliah.com/" />
        <meta
          property="og:image"
          content="https://pintarpilihkuliah.com/social-preview.webp"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://pintarpilihkuliah.com/social-preview.webp"
        />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceListSchema)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section className="landing-hero" aria-labelledby="hero-title">
        <div className="landing-hero__orb landing-hero__orb--one" />
        <div className="landing-hero__orb landing-hero__orb--two" />
        <div className="landing-shell landing-hero__grid">
          <div className="landing-hero__copy">
            <p className="landing-eyebrow">
              <Sparkles size={16} aria-hidden="true" /> Program Mentoring
              Beasiswa
            </p>
            <h1 id="hero-title">Mentoring persiapan kuliah ke kampus impian</h1>
            <p className="landing-hero__lead">
              Temukan peluang, siapkan berkas, dan selesaikan setiap tahap
              pendaftaran bersama mentor selama satu tahun.
            </p>
            <div className="landing-hero__actions">
              <button
                type="button"
                className="landing-button landing-button--primary"
                onClick={() => {
                  trackEvent("Conversion", "hero_cta_click", "pilih-program");
                  scrollToId("pilih-program");
                }}
              >
                Pilih Program <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="landing-button landing-button--secondary"
                onClick={() => scrollToId("kisah-siswa")}
              >
                Lihat Cerita Alumni
              </button>
            </div>
            <div
              className="landing-hero__quickfacts"
              aria-label="Ringkasan program"
            >
              <span>
                <Check size={15} aria-hidden="true" /> Mentoring Rutin
              </span>
              <span>
                <Check size={15} aria-hidden="true" /> 4 wilayah pilihan
              </span>
              <span>
                <Check size={15} aria-hidden="true" /> Akses 1 tahun
              </span>
            </div>
          </div>

          <div className="landing-hero__visual">
            <figure className="hero-photo hero-photo--main">
              <img
                src={acceptancePhoto}
                alt="Siswa menunjukkan dokumen penerimaan di lingkungan kampus"
                width="900"
                height="1132"
                fetchPriority="high"
              />
            </figure>
            <figure className="hero-photo hero-photo--secondary">
              <img
                src={departurePhoto}
                alt="Dokumentasi keberangkatan sekelompok pelajar"
                width="1200"
                height="541"
                loading="eager"
              />
            </figure>
            <div className="hero-mentoring-card">
              <span className="hero-mentoring-card__icon">
                <UsersRound size={21} aria-hidden="true" />
              </span>
              <span>
                <strong>Mentoring Rutin</strong>
                <small>Sesi online yang jagain kamu on-track</small>
              </span>
            </div>
            <div className="hero-spark" aria-hidden="true">
              ✦
            </div>
          </div>
        </div>
      </section>

      <section className="landing-proof" aria-label="Fasilitas utama program">
        <div className="landing-shell landing-proof__grid">
          <div className="landing-proof__mentoring">
            <Video size={27} aria-hidden="true" />
            <span>
              <strong>Mentoring Rutin</strong>
              <small>Sesi online yang jagain kamu on-track</small>
            </span>
          </div>
          <div>
            <strong>4</strong>
            <span>wilayah tujuan</span>
          </div>
          <div>
            <strong>1 tahun</strong>
            <span>akses program</span>
          </div>
          <div>
            <strong>Materi</strong>
            <span>webinar dan template</span>
          </div>
        </div>
      </section>

      <section
        className="landing-section landing-chooser"
        id="pilih-program"
        aria-labelledby="chooser-title"
      >
        <div className="landing-shell">
          <div className="landing-section__heading landing-section__heading--center">
            <p className="landing-eyebrow">Mulai dari posisi kamu sekarang</p>
            <h2 id="chooser-title">Sudah punya wilayah tujuan?</h2>
            <p>
              Pilih jalur yang paling sesuai. Kamu dapat langsung melihat
              program dan melanjutkan pendaftaran.
            </p>
          </div>

          <div
            className="decision-grid"
            role="group"
            aria-label="Pilih jalur program"
          >
            <button
              type="button"
              className={`decision-option decision-option--compare ${decision === "compare" ? "is-selected" : ""}`}
              onClick={() => handleDecision("compare")}
              aria-pressed={decision === "compare"}
            >
              <span className="decision-option__icon">
                <Globe2 size={31} aria-hidden="true" />
              </span>
              <span>
                <small>Masih membandingkan</small>
                <strong>Jelajahi semua wilayah</strong>
                <em>Indonesia, Asia Pasifik, Timur Tengah, dan Eropa.</em>
              </span>
              <ArrowRight size={22} aria-hidden="true" />
            </button>

            <button
              type="button"
              className={`decision-option decision-option--focused ${decision === "focused" ? "is-selected" : ""}`}
              onClick={() => handleDecision("focused")}
              aria-pressed={decision === "focused"}
            >
              <span className="decision-option__icon">
                <MapPinned size={31} aria-hidden="true" />
              </span>
              <span>
                <small>Sudah punya tujuan</small>
                <strong>Fokus ke satu wilayah</strong>
                <em>Pilih program yang sesuai dengan tujuan utamamu.</em>
              </span>
              <ArrowRight size={22} aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      <section
        className={`landing-section featured-program ${decision === "compare" ? "is-targeted" : ""}`}
        id="paket-semua-wilayah"
        aria-labelledby="all-regions-title"
        onMouseEnter={() => {
          interestProgramIdRef.current = allRegionsProgram.id;
        }}
        onFocusCapture={() => {
          interestProgramIdRef.current = allRegionsProgram.id;
        }}
      >
        <div className="featured-program__shape" aria-hidden="true" />
        <div className="landing-shell featured-program__grid">
          <div className="featured-program__content">
            <span className="program-label">Pilihan paling fleksibel</span>
            <h2 id="all-regions-title">Paket Semua Wilayah</h2>
            <p>{allRegionsProgram.description}</p>
            <ul className="scope-list" aria-label="Wilayah dalam paket">
              {allRegionsProgram.scope.map((item) => (
                <li key={item}>
                  <Check size={16} aria-hidden="true" /> {item}
                </li>
              ))}
            </ul>

            <a
              href={allRegionsProgram.checkoutUrl}
              className="landing-button landing-button--primary"
              onClick={() => handleCheckout(allRegionsProgram, "featured-cta")}
            >
              Pilih Paket Ini <ArrowRight size={18} aria-hidden="true" />
            </a>
            <p className="checkout-note">
              Kamu akan melanjutkan ke halaman pendaftaran dan pembayaran.
            </p>
          </div>

          <a
            href={allRegionsProgram.checkoutUrl}
            className="featured-program__image"
            onClick={() => handleCheckout(allRegionsProgram, "featured-image")}
            aria-label="Pilih Paket Semua Wilayah"
          >
            <span className="featured-program__sticker">
              4 wilayah <small>dalam 1 paket</small>
            </span>
            <img
              src={allRegionsProgram.image}
              alt="Ringkasan Program Mentoring Beasiswa Paket Semua Wilayah"
              width="900"
              height="900"
              loading="lazy"
              decoding="async"
            />
          </a>
        </div>
      </section>

      <section
        className="landing-section regional-section"
        id="program-wilayah"
        aria-labelledby="regional-title"
      >
        <div className="landing-shell">
          <div className="regional-heading">
            <div className="landing-section__heading">
              <p className="landing-eyebrow">Pilihan program</p>
              <h2 id="regional-title">Fokus pada satu wilayah</h2>
              <p>Pilih wilayah yang paling dekat dengan rencanamu saat ini.</p>
            </div>
            <div className="regional-heading__note">
              <Compass size={24} aria-hidden="true" />
              <span>
                <strong>Belum yakin?</strong>
                <small>Paket Semua Wilayah membantumu membandingkan.</small>
              </span>
            </div>
          </div>

          <div className="program-grid">
            {regionalPrograms.map((program, index) => (
              <article
                className={`program-card program-card--${program.id}`}
                key={program.id}
                onMouseEnter={() => {
                  interestProgramIdRef.current = program.id;
                }}
                onFocusCapture={() => {
                  interestProgramIdRef.current = program.id;
                }}
              >
                <a
                  href={program.checkoutUrl}
                  className="program-card__visual"
                  onClick={() => handleCheckout(program, "regional-image")}
                  aria-label={`Pilih ${program.name}`}
                >
                  <span className="program-card__number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <img
                    src={program.image}
                    alt={`Ringkasan ${program.name}`}
                    width="900"
                    height="900"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
                <div className="program-card__body">
                  <span>Program wilayah</span>
                  <h3>{program.shortName}</h3>
                  <p>{program.description}</p>
                  <div className="program-card__scope">
                    {program.scope.map((item) => (
                      <small key={item}>{item}</small>
                    ))}
                  </div>
                  <a
                    href={program.checkoutUrl}
                    className="landing-button landing-button--primary program-card__button"
                    onClick={() => handleCheckout(program, "regional-cta")}
                  >
                    Pilih Program Ini{" "}
                    <ArrowRight size={17} aria-hidden="true" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="landing-section benefits-section"
        id="mentoring"
        aria-labelledby="benefits-title"
      >
        <div className="landing-shell">
          <div className="mentoring-focus">
            <div className="mentoring-focus__visual">
              <img
                src={mentoringSession}
                alt="Ilustrasi siswa mengikuti sesi mentoring daring"
                width="1200"
                height="800"
                loading="lazy"
                decoding="async"
              />
              <div className="mentoring-focus__badge">
                <MessageCircleMore size={21} aria-hidden="true" />
                <span>
                  <strong>Rutin berdiskusi</strong>
                  <small>agar langkah tetap terarah</small>
                </span>
              </div>
            </div>
            <div className="mentoring-focus__copy">
              <p className="landing-eyebrow">Inti dari program</p>
              <h2 id="benefits-title">Punya arah jelas di setiap tahap</h2>
              <p>
                Mentoring Rutin membantumu memahami pilihan, menyusun prioritas,
                dan menuntaskan persiapan secara bertahap.
              </p>
              <span className="mentoring-focus__zoom">
                <Video size={17} aria-hidden="true" /> Diskusikan progres dan
                langkah berikutnya langsung melalui Zoom
              </span>
            </div>
          </div>

          <div className="benefit-grid">
            {benefits.map(({ icon: Icon, title, description }, index) => (
              <article className="benefit-item" key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <Icon size={24} aria-hidden="true" />
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="landing-section stories-section"
        id="kisah-siswa"
        aria-labelledby="stories-title"
      >
        <div className="landing-shell">
          <div className="landing-section__heading">
            <p className="landing-eyebrow">Cerita Alumni</p>
            <h2 id="stories-title">Mereka sudah memulai langkahnya</h2>
            <p>
              Lihat pengalaman alumni dan perjalanan pelajar #PintarPilihKuliah
              menuju kampus tujuan.
            </p>
          </div>

          <div className="testimonial-layout">
            <article className="testimonial-feature">
              <div className="testimonial-feature__photo">
                <img
                  src={pelangiPhoto}
                  alt="Pelangi Firdaus di lingkungan kampus"
                  width="1200"
                  height="1600"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="testimonial-feature__quote">
                <span className="quote-mark" aria-hidden="true">
                  “
                </span>
                <blockquote>
                  Alhamdulillah, saya sangat bersyukur telah bergabung dengan
                  #PintarPilihKuliah. Dari awal hingga akhir, mereka selalu
                  memberikan dukungan dan bimbingan yang sangat baik.
                </blockquote>
                <div>
                  <strong>Pelangi Firdaus</strong>
                  <span>Mahasiswa S1, Universitas Al-Azhar, Mesir</span>
                  <small>Alumni 2024</small>
                </div>
              </div>
            </article>

            <article className="testimonial-note">
              <span className="quote-mark" aria-hidden="true">
                “
              </span>
              <blockquote>
                Saya mendapatkan banyak insight dan arahan untuk mempersiapkan
                berkas beasiswa yang berkualitas. Alhamdulillah, saya berhasil
                mendapatkan beasiswa penuh di jurusan Syariah Islamiyah.
              </blockquote>
              <div>
                <span className="testimonial-note__avatar">FA</span>
                <span>
                  <strong>Fatimah Azzahra</strong>
                  <small>Mahasiswa S1, Al-Azhar University, Mesir</small>
                </span>
              </div>
            </article>
          </div>

          <div
            className="story-gallery"
            aria-label="Dokumentasi kegiatan siswa"
          >
            <figure className="story-gallery__wide">
              <img
                src={departurePhoto}
                alt="Sekelompok pelajar bersiap untuk keberangkatan"
                width="1200"
                height="541"
                loading="lazy"
                decoding="async"
              />
              <figcaption>Momen keberangkatan pelajar</figcaption>
            </figure>
            <figure>
              <img
                src={egyptStudentsPhoto}
                alt="Sekelompok pelajar menjalani kegiatan di Mesir"
                width="900"
                height="1200"
                loading="lazy"
                decoding="async"
              />
              <figcaption>Perjalanan belajar di Mesir</figcaption>
            </figure>
            <figure>
              <img
                src={malaysiaPhoto}
                alt="Dokumentasi kunjungan informasi Universiti Malaya"
                width="900"
                height="1600"
                loading="lazy"
                decoding="async"
              />
              <figcaption>Menjelajahi pilihan studi Asia Pasifik</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section
        className="landing-section process-section"
        id="cara-kerja"
        aria-labelledby="process-title"
      >
        <div className="landing-shell process-layout">
          <div className="landing-section__heading">
            <p className="landing-eyebrow">Cara memulai</p>
            <h2 id="process-title">Tiga langkah menuju persiapan yang jelas</h2>
          </div>
          <div className="process-line">
            <article>
              <span>01</span>
              <Compass size={27} aria-hidden="true" />
              <h3>Pilih cakupan</h3>
              <p>Tentukan satu wilayah atau pilih Paket Semua Wilayah.</p>
            </article>
            <article>
              <span>02</span>
              <ClipboardCheck size={27} aria-hidden="true" />
              <h3>Susun persiapan</h3>
              <p>Gunakan kalender, checklist, informasi, dan materi program.</p>
            </article>
            <article>
              <span>03</span>
              <GraduationCap size={27} aria-hidden="true" />
              <h3>Lanjutkan tiap langkah</h3>
              <p>Bahas progres dan langkah berikutnya dalam Mentoring Rutin.</p>
            </article>
          </div>
        </div>
      </section>

      <section
        className="landing-section faq-section"
        id="faq"
        aria-labelledby="faq-title"
      >
        <div className="landing-shell faq-layout">
          <div>
            <p className="landing-eyebrow">Sebelum mendaftar</p>
            <h2 id="faq-title">Pertanyaan yang sering muncul</h2>
            <p>
              Masih bingung memilih? Tanya admin melalui tombol WhatsApp yang
              selalu tersedia di layar.
            </p>
          </div>
          <Accordion type="single" collapsible className="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem value={`faq-${index}`} key={faq.question}>
                <AccordionTrigger
                  onClick={() =>
                    trackEvent("Engagement", "faq_open", faq.question)
                  }
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent>
                  <p>{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section
        className="landing-section community-section"
        id="komunitas-gratis"
        aria-labelledby="community-title"
      >
        <div className="landing-shell community-section__grid">
          <span className="community-section__icon" aria-hidden="true">
            <MessageCircleMore size={34} />
          </span>
          <div>
            <h2 id="community-title">Mulai dari komunitas gratis</h2>
            <p>
              Ikuti informasi peluang beasiswa di Grup WhatsApp
              #PintarPilihKuliah. Mentoring dan grup peserta eksklusif tersedia
              di program berbayar.
            </p>
          </div>
          <a
            href={settings.wa_group_link}
            className="landing-button landing-button--primary community-section__button"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackEvent("Conversion", "whatsapp_group_click", "community")
            }
          >
            Gabung Komunitas Gratis
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="landing-final" aria-labelledby="final-title">
        <div className="landing-final__decoration" aria-hidden="true">
          ✦
        </div>
        <div className="landing-shell landing-final__content">
          <div>
            <p className="landing-eyebrow">Mulai saat kamu siap</p>
            <h2 id="final-title">Pilih tujuanmu. Siapkan langkahnya.</h2>
            <p>
              Temukan program yang sesuai, lalu lanjutkan pendaftaran dalam
              beberapa langkah.
            </p>
          </div>
          <button
            type="button"
            className="landing-button landing-button--primary"
            onClick={() => scrollToId("pilih-program")}
          >
            Pilih Program <ArrowRight size={18} aria-hidden="true" />
          </button>
        </div>
      </section>

      <div className="mobile-conversion-bar" aria-label="Akses cepat">
        <button type="button" onClick={() => scrollToId("pilih-program")}>
          Pilih Program
        </button>
      </div>

      {showOfferRecovery && !showExitOffer && (
        <button
          type="button"
          className="coupon-recovery"
          onClick={handleReopenRetentionOffer}
          aria-label={
            retentionCouponCopied
              ? "Lihat kembali kupon yang sudah disalin"
              : "Buka kembali penawaran kupon"
          }
        >
          <TicketPercent size={20} aria-hidden="true" />
          <span>
            {retentionCouponCopied
              ? "Lihat kupon tersimpan"
              : "Lihat penawaran"}
          </span>
        </button>
      )}

      {showExitOffer && (
        <div
          className="exit-offer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              handleDismissRetentionOffer("backdrop");
            }
          }}
        >
          <section
            className="exit-offer__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-offer-title"
          >
            <button
              type="button"
              className="exit-offer__close"
              aria-label="Tutup penawaran"
              onClick={() => handleDismissRetentionOffer("close")}
            >
              <X size={20} aria-hidden="true" />
            </button>
            <span className="exit-offer__icon">
              <TicketPercent size={25} aria-hidden="true" />
            </span>
            <p className="landing-eyebrow">Sebelum kamu pergi</p>
            <h2 id="exit-offer-title">Masih mempertimbangkan programnya?</h2>
            <p>
              Ada potongan untuk Program {retentionProgram.shortName}. Salin
              kodenya, lalu lanjutkan saat kamu siap.
            </p>
            <button
              type="button"
              className="exit-offer__coupon"
              onClick={() =>
                handleCopyCoupon(retentionCoupon.code, retentionProgram.id)
              }
            >
              <span>
                <small>Penawaran khusus</small>
                <strong>{retentionCoupon.label}</strong>
              </span>
              <span>
                {copiedCoupon === retentionCoupon.code ? (
                  <Check size={16} aria-hidden="true" />
                ) : (
                  <Copy size={16} aria-hidden="true" />
                )}
                {copiedCoupon === retentionCoupon.code || retentionCouponCopied
                  ? "Kode tersalin"
                  : retentionCoupon.code}
              </span>
            </button>
            {copyError === retentionProgram.id && (
              <p className="exit-offer__error">
                Penyalinan otomatis gagal. Salin kode secara manual.
              </p>
            )}
            <a
              href={retentionProgram.checkoutUrl}
              className="landing-button landing-button--primary"
              onClick={() => handleCheckout(retentionProgram, "retention")}
            >
              Lanjutkan Pendaftaran <ArrowRight size={18} aria-hidden="true" />
            </a>
            <button
              type="button"
              className="exit-offer__later"
              onClick={() => handleDismissRetentionOffer("continue")}
            >
              Lanjut melihat program
            </button>
            <small className="exit-offer__terms">
              Ketersediaan dan ketentuan promo mengikuti halaman pembayaran.
            </small>
          </section>
        </div>
      )}
    </div>
  );
};

export default MentoringHome;
